import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { GlobeAltIcon, PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';

interface DnsRecord {
  id: string;
  subDomain: string;
  fieldType: string;
  target: string;
  ttl?: number;
}

interface NewRecord {
  subDomain: string;
  fieldType: string;
  target: string;
  ttl: number;
}

interface DomainSearchResult {
  domain: string;
  available: boolean;
  price_ht: number;
  price_ttc: number;
  renewal_ht: number;
  renewal_ttc: number;
}

export default function Domains() {
  const [domains, setDomains] = useState<string[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [recordsLoading, setRecordsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<DnsRecord | null>(null);

  // Domain search / order state
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<DomainSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchHasMore, setSearchHasMore] = useState<boolean>(false);
  const [searchNextPage, setSearchNextPage] = useState<number>(1);
  const [moreLoading, setMoreLoading] = useState<boolean>(false);
  const [orderingDomain, setOrderingDomain] = useState<string | null>(null);
  const [confirmOrder, setConfirmOrder] = useState<DomainSearchResult | null>(null);
  const [placing, setPlacing] = useState<boolean>(false);

  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const location = useLocation();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [newRecord, setNewRecord] = useState<NewRecord>({
    subDomain: '',
    fieldType: 'A',
    target: '',
    ttl: 3600,
  });

  useEffect(() => {
    fetchDomains();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && domains.length > 0) {
      const params = new URLSearchParams(location.search);
      const selected = params.get('selected');
      if (selected && domains.includes(selected)) {
        setSelectedDomain(selected);
      }
    }
  }, [loading, domains, location.search]);

  useEffect(() => {
    if (selectedDomain) {
      fetchRecords(selectedDomain);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/domains`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch domains');
      const data = await response.json();
      setDomains(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching domains:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (domain: string) => {
    try {
      setRecordsLoading(true);
      const response = await fetch(`${API_URL}/domains/${domain}/records`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch DNS records');
      const data = await response.json();
      setRecords(data || []);
    } catch (err: any) {
      console.error('Error fetching records:', err);
      setRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/domains/${selectedDomain}/records`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${keycloak?.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRecord),
      });
      if (!response.ok) throw new Error('Failed to create DNS record');
      setShowAddModal(false);
      setNewRecord({ subDomain: '', fieldType: 'A', target: '', ttl: 3600 });
      fetchRecords(selectedDomain!);
    } catch (err: any) {
      toast('Error creating record: ' + err.message, 'error');
    }
  };

  const handleUpdateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/domains/${selectedDomain}/records/${editingRecord!.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${keycloak?.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subDomain: editingRecord!.subDomain,
          fieldType: editingRecord!.fieldType,
          target: editingRecord!.target,
          ttl: editingRecord!.ttl,
        }),
      });
      if (!response.ok) throw new Error('Failed to update DNS record');
      setEditingRecord(null);
      fetchRecords(selectedDomain!);
    } catch (err: any) {
      toast('Error updating record: ' + err.message, 'error');
    }
  };

  const handleDeleteRecord = async (recordId: string) => {
    const ok = await confirm({ title: 'Delete DNS record', message: 'Are you sure you want to delete this DNS record?', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/domains/${selectedDomain}/records/${recordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to delete DNS record');
      fetchRecords(selectedDomain!);
    } catch (err: any) {
      toast('Error deleting record: ' + err.message, 'error');
    }
  };

  // --- Domain search / order ---

  const openSearchModal = () => {
    setShowSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setSearchHasMore(false);
    setSearchNextPage(1);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setSearchHasMore(false);
    try {
      const res = await fetch(`${API_URL}/domains/search`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery.trim(), page: 0 }),
      });
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setSearchResults(data.results || []);
      setSearchHasMore(data.has_more ?? false);
      setSearchNextPage(data.next_page ?? 1);
    } catch (err: any) {
      setSearchError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleLoadMore = async () => {
    setMoreLoading(true);
    try {
      const res = await fetch(`${API_URL}/domains/search`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery.trim(), page: searchNextPage }),
      });
      if (!res.ok) throw new Error('Failed to load more');
      const data = await res.json();
      setSearchResults(prev => [...prev, ...(data.results || [])]);
      setSearchHasMore(data.has_more ?? false);
      setSearchNextPage(data.next_page ?? searchNextPage + 1);
    } catch (err: any) {
      toast('Error loading more results: ' + err.message, 'error');
    } finally {
      setMoreLoading(false);
    }
  };

  const handleOrderDomain = (result: DomainSearchResult) => {
    setConfirmOrder(result);
  };

  const handlePlaceOrder = async () => {
    if (!confirmOrder) return;
    setPlacing(true);
    setOrderingDomain(confirmOrder.domain);
    try {
      const res = await fetch(`${API_URL}/orders/domain`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domainName: confirmOrder.domain,
          priceHT: confirmOrder.price_ht,
          priceTTC: confirmOrder.price_ttc,
          renewalHT: confirmOrder.renewal_ht,
          renewalTTC: confirmOrder.renewal_ttc,
        }),
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to place order'); }
      toast(`Order placed for ${confirmOrder.domain}! Check the Orders page for status.`, 'success');
      setConfirmOrder(null);
      setShowSearchModal(false);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setPlacing(false);
      setOrderingDomain(null);
    }
  };

  const formatPrice = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(value);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<GlobeAltIcon />}
          title="Domains"
          description="Manage your domains and DNS records."
          actions={
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary"
              disabled
            >
              Order new Domain
            </button>
          }
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<GlobeAltIcon />}
        title="Domains"
        description="Manage your domains and DNS records."
        actions={
          <button
            type="button"
            onClick={openSearchModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring"
          >
            Order new Domain
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Domains List */}
        <div className="lg:col-span-1">
          <div className="bg-card shadow rounded-lg">
            <div className="px-4 py-5 border-b border-border sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-foreground">Your Domains</h3>
            </div>
            {domains.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">No domains yet.</p>
                <button type="button" onClick={openSearchModal} className="mt-2 text-sm text-primary hover:underline">
                  Order your first domain
                </button>
              </div>
            ) : (
            <ul className="divide-y divide-border">
              {domains.map((domain) => (
                <li
                  key={domain}
                  onClick={() => setSelectedDomain(domain)}
                  className={`px-4 py-4 cursor-pointer hover:bg-muted/40 transition ${
                    selectedDomain === domain ? 'bg-primary/10 border-l-4 border-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground truncate">{domain}</p>
                    {selectedDomain === domain && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                        Selected
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            )}
          </div>
        </div>

        {/* DNS Records */}
        <div className="lg:col-span-2">
          {selectedDomain ? (
            <div className="bg-card shadow rounded-lg">
              <div className="px-4 py-5 border-b border-border sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-foreground">DNS Records for {selectedDomain}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{records.length} record{records.length !== 1 ? 's' : ''}</p>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Add Record
                </button>
              </div>

              {recordsLoading ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : records.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Subdomain</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Target</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">TTL</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {records.map((record) => (
                        <tr key={record.id} className="hover:bg-muted/40">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{record.subDomain || '@'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success ring-1 ring-inset ring-success/20">{record.fieldType}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground max-w-xs truncate">{record.target}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{record.ttl || 'Default'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button onClick={() => setEditingRecord(record)} className="text-primary hover:text-primary/80 mr-4">
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => handleDeleteRecord(record.id)} className="text-red-600 hover:text-red-900">
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-sm text-muted-foreground">No DNS records found for this domain.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card shadow rounded-lg">
              <div className="text-center py-12">
                <p className="text-sm text-muted-foreground">Select a domain to view and manage DNS records</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Domain Search / Order Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => setShowSearchModal(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-top bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">Search for a domain name</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Enter a name to check availability and pricing.</p>
                  </div>
                  <button type="button" onClick={() => setShowSearchModal(false)} className="text-muted-foreground hover:text-muted-foreground">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                <form onSubmit={handleSearch} className="flex gap-3 mb-6">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="edj-labs or edj-labs.com"
                      className="block w-full pl-10 pr-3 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-ring focus:border-primary"
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!searchQuery.trim() || searchLoading}
                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searchLoading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Searching…</>
                    ) : 'Search'}
                  </button>
                </form>

                {searchError && (
                  <div className="rounded-md bg-destructive/10 p-3 mb-4">
                    <p className="text-sm text-red-700">{searchError}</p>
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/30">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Domain</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">1st year (excl. VAT)</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Renewal/yr (excl. VAT)</th>
                            <th className="px-4 py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                          {searchResults.map((result) => (
                            <tr key={result.domain} className="hover:bg-muted/40">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-foreground">{result.domain}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                {result.available ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                                    <span className="h-1.5 w-1.5 rounded-full bg-success/100 inline-block" />
                                    Available
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gray-400 inline-block" />
                                    Unavailable
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground text-right">
                                {result.available ? formatPrice(result.price_ht) : '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground text-right">
                                {result.available ? formatPrice(result.renewal_ht) : '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                {result.available && (
                                  <button
                                    type="button"
                                    onClick={() => handleOrderDomain(result)}
                                    disabled={orderingDomain === result.domain}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {orderingDomain === result.domain ? 'Ordering…' : 'Order'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {searchHasMore && (
                      <div className="mt-4 text-center">
                        <button
                          type="button"
                          onClick={handleLoadMore}
                          disabled={moreLoading}
                          className="inline-flex items-center px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground bg-card hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {moreLoading ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2" />Loading 25 more extensions…</>
                          ) : 'Show 25 more extensions'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!searchLoading && searchResults.length === 0 && !searchError && (
                  <div className="text-center py-12 text-muted-foreground">
                    <MagnifyingGlassIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">Search for a domain name to see availability and pricing.</p>
                  </div>
                )}
              </div>

              <div className="bg-muted/30 px-4 py-3 sm:px-6 text-right">
                <button type="button" onClick={() => setShowSearchModal(false)} className="inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-sm font-medium text-foreground hover:bg-muted/40">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Order Confirmation Modal */}
      {confirmOrder && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => !placing && setConfirmOrder(null)} />
            <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-md z-10 overflow-hidden">
              <div className="bg-primary px-6 py-4">
                <h3 className="text-lg font-semibold text-white">Confirm Domain Order</h3>
                <p className="text-white/80 text-sm mt-0.5">{confirmOrder.domain}</p>
              </div>
              <div className="px-6 py-5 space-y-4">
                {/* First year */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">First Year</p>
                  <div className="bg-muted/30 rounded-lg divide-y divide-border">
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">Registration ({confirmOrder.domain})</span>
                      <span className="font-medium text-foreground">{formatPrice(confirmOrder.price_ht)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">Total excl. VAT</span>
                      <span className="font-semibold text-foreground">{formatPrice(confirmOrder.price_ht)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">VAT (20%)</span>
                      <span className="text-muted-foreground">{formatPrice(confirmOrder.price_ttc - confirmOrder.price_ht)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm bg-card rounded-b-lg">
                      <span className="font-semibold text-foreground">Total incl. VAT</span>
                      <span className="font-bold text-primary text-base">{formatPrice(confirmOrder.price_ttc)}</span>
                    </div>
                  </div>
                </div>
                {/* Renewal */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Renewal (per year)</p>
                  <div className="bg-muted/30 rounded-lg divide-y divide-border">
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">Renewal excl. VAT</span>
                      <span className="font-medium text-foreground">{formatPrice(confirmOrder.renewal_ht)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">VAT (20%)</span>
                      <span className="text-muted-foreground">{formatPrice(confirmOrder.renewal_ttc - confirmOrder.renewal_ht)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm bg-card rounded-b-lg">
                      <span className="font-semibold text-foreground">Renewal incl. VAT</span>
                      <span className="font-bold text-foreground">{formatPrice(confirmOrder.renewal_ttc)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-muted/30 px-6 py-4 flex justify-end gap-3">
                <button type="button" onClick={() => setConfirmOrder(null)} disabled={placing}
                  className="inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-sm font-medium text-foreground hover:bg-muted/40 disabled:opacity-50 cursor-pointer">
                  Cancel
                </button>
                <button type="button" onClick={handlePlaceOrder} disabled={placing}
                  className="inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-primary text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 cursor-pointer">
                  {placing ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Placing order…</> : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Record Modal */}
      {showAddModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => setShowAddModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-card rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-foreground">Add DNS Record</h3>
                <div className="mt-4">
                  <form onSubmit={handleAddRecord} className="space-y-4">
                    <div className="text-left">
                      <label htmlFor="subdomain" className="block text-sm font-medium text-foreground">Subdomain</label>
                      <input type="text" id="subdomain" value={newRecord.subDomain}
                        onChange={(e) => setNewRecord({ ...newRecord, subDomain: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm"
                        placeholder="www or leave empty for root" />
                    </div>
                    <div className="text-left">
                      <label htmlFor="type" className="block text-sm font-medium text-foreground">Type</label>
                      <select id="type" value={newRecord.fieldType}
                        onChange={(e) => setNewRecord({ ...newRecord, fieldType: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm">
                        {['A','AAAA','CNAME','MX','TXT','NS','SRV'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="text-left">
                      <label htmlFor="target" className="block text-sm font-medium text-foreground">Target</label>
                      <input type="text" id="target" required value={newRecord.target}
                        onChange={(e) => setNewRecord({ ...newRecord, target: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm"
                        placeholder="192.0.2.1 or example.com" />
                    </div>
                    <div className="text-left">
                      <label htmlFor="ttl" className="block text-sm font-medium text-foreground">TTL (seconds)</label>
                      <input type="number" id="ttl" value={newRecord.ttl}
                        onChange={(e) => setNewRecord({ ...newRecord, ttl: parseInt(e.target.value) })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm" />
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                      <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none sm:col-start-2 sm:text-sm">Add Record</button>
                      <button type="button" onClick={() => setShowAddModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm">Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Record Modal */}
      {editingRecord && (
        <div className="fixed z-10 inset-0 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" onClick={() => setEditingRecord(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-card rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-foreground">Edit DNS Record</h3>
                <div className="mt-4">
                  <form onSubmit={handleUpdateRecord} className="space-y-4">
                    <div className="text-left">
                      <label htmlFor="edit-subdomain" className="block text-sm font-medium text-foreground">Subdomain</label>
                      <input type="text" id="edit-subdomain" value={editingRecord.subDomain}
                        onChange={(e) => setEditingRecord({ ...editingRecord, subDomain: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm" />
                    </div>
                    <div className="text-left">
                      <label htmlFor="edit-type" className="block text-sm font-medium text-foreground">Type</label>
                      <select id="edit-type" value={editingRecord.fieldType}
                        onChange={(e) => setEditingRecord({ ...editingRecord, fieldType: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm">
                        {['A','AAAA','CNAME','MX','TXT','NS','SRV'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="text-left">
                      <label htmlFor="edit-target" className="block text-sm font-medium text-foreground">Target</label>
                      <input type="text" id="edit-target" required value={editingRecord.target}
                        onChange={(e) => setEditingRecord({ ...editingRecord, target: e.target.value })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm" />
                    </div>
                    <div className="text-left">
                      <label htmlFor="edit-ttl" className="block text-sm font-medium text-foreground">TTL (seconds)</label>
                      <input type="number" id="edit-ttl" value={editingRecord.ttl || 3600}
                        onChange={(e) => setEditingRecord({ ...editingRecord, ttl: parseInt(e.target.value) })}
                        className="mt-1 block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm" />
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                      <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none sm:col-start-2 sm:text-sm">Update Record</button>
                      <button type="button" onClick={() => setEditingRecord(null)} className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm">Cancel</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
